import React, { useEffect, useState } from "react";

import { Button, Collapse, constants, useWindowSize } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import { Node } from "@tiptap/core";
import { Editor, NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import { AssignmentConfig } from "@/types/models";
import {
  ICheckForUnderstandingQuizAnswerOption,
  IChecksForUnderstanding,
  IChecksForUnderstandingContextSubmissions,
} from "@/types/models/CheckForUnderstandingQuiz.interface";
import {
  ACTIONS_TOOLBAR_HEIGHT,
  SMALL_SCREEN_ACTIONS_TOOLBAR_HEIGHT,
} from "@/view/ActionsToolbar/ActionsToolbar.style";
import { CONFIG_KEYS } from "@/view/AdditionalAssignmentSettings/AdditionalAssignmentSettings.constants";
import focusElementRefKeys from "@/view/constants/focusElementRefKeys";
import { useDynamicDataProvider } from "@/view/TiptapArticleBody/extensions/DataProvider/useDynamicDataProvider";
import getAssignmentSetting from "@/view/utils/getAssignmentSetting";
import useFocusManager from "@/view/utils/useFocusManager";
import usePresentModeContext from "@/view/utils/usePresentModeContext";
import {
  borderWrapperCss,
  collapseButtonCss,
  innerWrapperCss,
  outerWrapperCss,
} from "./ChecksForUnderstanding.style";
import { ChecksForUnderstandingBody } from "./ChecksForUnderstandingBody/ChecksForUnderstandingBody";
import {
  ChecksForUnderstandingHeader,
  HeaderContent,
} from "./ChecksForUnderstandingHeader/ChecksForUnderstandingHeader";

interface IProps {
  node: {
    attrs: {
      isActiveQuizQuestion: boolean;
      isCorrectSubmission: boolean | null;
      quiz: IChecksForUnderstanding;
    };
  };
  updateAttributes: (args: Record<string, string | boolean>) => void;
  editor: Editor;
}

const ButtonWithTrackEvent = withTrackEvent(Button);

export const formatSubmissions = (
  submissions: IChecksForUnderstandingContextSubmissions | undefined,
  questionId: string,
): ICheckForUnderstandingQuizAnswerOption[] => {
  // get all submissions for the current question
  const previousSubmissions = submissions?.[questionId] || [];

  return (previousSubmissions || []).map(({ id, is_correct }) => ({
    id,
    is_correct,
  }));
};

export const ChecksForUnderstandingComponent = ({
  node,
  updateAttributes,
  editor,
}: IProps) => {
  const { width: windowWidth } = useWindowSize();
  const {
    assignment,
    checksForUnderstandingSubmissions,
    currentFontSize,
    hasReadAloudInSession,
    isListenModeActive,
    userProperties,
    isStudentPreviewOn,
    addChecksForUnderstandingSubmissions,
    setIsReadAloudModeActive,
    isTranslateMode,
  } = useDynamicDataProvider(editor);
  const isChecksForUnderstandingVisible = !getAssignmentSetting(
    CONFIG_KEYS.hide_checks_for_understanding as keyof AssignmentConfig,
    assignment,
  );
  const {
    breakpoints: {
      Breakpoints: { sm },
    },
  } = constants;
  const isSmallScreen = windowWidth <= sm;
  const [selectedAnswer, setSelectedAnswer] =
    useState<ICheckForUnderstandingQuizAnswerOption>({
      id: "",
      text: "",
      is_correct: false,
    });

  const { setFocusManagerRef } = useFocusManager();

  // TODO: All attribute data passing will be removed when we migrate CFU to be a decoration.
  const data = node?.attrs?.quiz;
  const isStaleAssignmentIdInData =
    data &&
    // @ts-ignore content_id in the empty assignment
    assignment?.content_id &&
    // @ts-ignore content_id in the empty assignment
    data?.assignmentId !== assignment?.content_id;
  if (isStaleAssignmentIdInData) {
    // assignmentId in the node attributes data can get stale when `insertChecksForUnderstanding` was executed before
    // the relevant assignment was loaded. In that case, the attributes would not get refreshed due to `hasUninsertedCheckpoints`
    // that prevents re-insertion of the CFUs, with `assignmentId` referencing a stale value because it's not refreshed.
    // Note that this won't be an issue after we move to the decoration approach because we'll stop injecting CFU nodes
    // into the document.
    // @ts-ignore content_id in the empty assignment
    data.assignmentId = assignment.content_id;
  }
  const { id: questionId = "" } = data?.questionData || {};

  const initialSubmittedAnswers =
    formatSubmissions(checksForUnderstandingSubmissions, questionId) || [];
  const [submittedAnswers, setSubmittedAnswers] = useState<
    ICheckForUnderstandingQuizAnswerOption[]
  >(initialSubmittedAnswers);
  const { isTeacher = false } = userProperties;

  useEffect(() => {
    /* initialSubmittedAnswers is the source of truth for stored submissions and changes to initialSubmittedAnswers should override the value of submittedAnswers.
    However, child components temporarily override the submittedAnswers state to update the UI while minimizing additional GET submission requests. */
    setSubmittedAnswers(initialSubmittedAnswers);
  }, [JSON.stringify(initialSubmittedAnswers)]);

  // If user has already submitted a correct answer then we need to call updateAttributes  to unblur the nodes associated with this check for understanding

  if (
    (!isTeacher || isStudentPreviewOn) &&
    submittedAnswers.some((answer) => answer.is_correct)
  ) {
    updateAttributes({
      isActiveQuizQuestion: false,
      isCorrectSubmission: true,
    });
  }

  if (submittedAnswers?.length > 0 && !node?.attrs?.isCorrectSubmission) {
    updateAttributes({
      isActiveQuizQuestion: true,
    });
  }

  const { isPresentModeActive } = usePresentModeContext();

  const { status } = HeaderContent(
    submittedAnswers,
    isTeacher,
    isStudentPreviewOn,
  );
  const hasSubmittedCorrectAnswer = status === "correct";
  const { isHidden: isCfuBodyHidden, onClick: setIsCfuBodyHidden } =
    Collapse.useCollapse({
      isHidden: hasSubmittedCorrectAnswer,
    });

  useEffect(() => {
    if (isTeacher && !isStudentPreviewOn) {
      // reset submitted answers when a teacher is not in student preview mode
      setSubmittedAnswers([] as ICheckForUnderstandingQuizAnswerOption[]);

      // ensure nodes that are unblurred in student preview are reset when a teacher exits student preview mode
      updateAttributes({
        isCorrectSubmission: false,
      });

      if (isCfuBodyHidden) {
        // @ts-ignore next line because we don't have an event object to pass. All we need is to toggle the CFU question box
        setIsCfuBodyHidden();
      }
    }
  }, [isStudentPreviewOn]);

  useEffect(() => {
    if (
      submittedAnswers.length &&
      submittedAnswers.length !== initialSubmittedAnswers.length
    ) {
      addChecksForUnderstandingSubmissions({ [questionId]: submittedAnswers });
      if (hasSubmittedCorrectAnswer && (!isTeacher || isStudentPreviewOn)) {
        // @ts-ignore next line because we don't have an event object to pass. All we need is to toggle the CFU question box and
        // calling setIsCfuBodyHidden will do just that.
        setIsCfuBodyHidden();
      }
    }
  }, [JSON.stringify(submittedAnswers)]);

  if (!data || !data.questionData || !isChecksForUnderstandingVisible)
    return null;

  const isActiveQuizQuestion = node?.attrs?.isActiveQuizQuestion;
  const isFirstQuizQuestion = data?.questionData?.question_number === 1;

  const renderChecksForUnderstandingHeader = () => {
    if (hasSubmittedCorrectAnswer) {
      return (
        <ButtonWithTrackEvent
          __cssFor={{ root: collapseButtonCss }}
          actionName={actionNames.hide}
          objectType={objectTypes.quiz}
          ariaProps={{
            "aria-controls": "checks-for-understanding-quiz",
            "aria-expanded": !isCfuBodyHidden,
          }}
          flavor={Button.flavors.incognito}
          onClick={setIsCfuBodyHidden}
          dataProps={{
            "data-testid": "cfu-collapsible",
            "data-qa-selector": `checks_for_understanding-${isCfuBodyHidden ? "show" : "hide"}`,
          }}
          streamProperties={{
            description: `${isCfuBodyHidden ? "Show" : "Hide"} checks for understanding quiz as a student`,
          }}
        >
          <ChecksForUnderstandingHeader
            currentFontSize={currentFontSize}
            data={data}
            isActiveQuizQuestion={isActiveQuizQuestion}
            isCfuBodyHidden={isCfuBodyHidden}
            isTeacher={isTeacher}
            isStudentPreviewOn={isStudentPreviewOn}
            submittedAnswers={submittedAnswers}
          />
        </ButtonWithTrackEvent>
      );
    } else {
      return (
        <ChecksForUnderstandingHeader
          currentFontSize={currentFontSize}
          data={data}
          isActiveQuizQuestion={isActiveQuizQuestion}
          isTeacher={isTeacher}
          isStudentPreviewOn={isStudentPreviewOn}
          submittedAnswers={submittedAnswers}
        />
      );
    }
  };
  return (
    <NodeViewWrapper as="span" css={outerWrapperCss}>
      <div
        css={[
          innerWrapperCss,
          {
            scrollMarginTop: isSmallScreen
              ? SMALL_SCREEN_ACTIONS_TOOLBAR_HEIGHT
              : ACTIONS_TOOLBAR_HEIGHT,
          },
          isTranslateMode ? borderWrapperCss : {},
        ]}
        ref={(ref: React.ElementRef<"div">) => {
          return isFirstQuizQuestion
            ? setFocusManagerRef(
                ref,
                focusElementRefKeys.checkForUnderstandingQuizQuestion,
              )
            : null;
        }}
      >
        <Collapse
          isHidden={isCfuBodyHidden}
          renderCustomTrigger={renderChecksForUnderstandingHeader}
        >
          <ChecksForUnderstandingBody
            currentFontSize={currentFontSize}
            data={data}
            hasReadAloudInSession={hasReadAloudInSession}
            isTeacher={isTeacher}
            isStudentPreviewOn={isStudentPreviewOn}
            isListenModeActive={isListenModeActive}
            isPresentModeActive={isPresentModeActive}
            setIsReadAloudModeActive={setIsReadAloudModeActive}
            setSelectedAnswer={setSelectedAnswer}
            selectedAnswer={selectedAnswer}
            submittedAnswers={submittedAnswers}
            setSubmittedAnswers={(answers) => {
              setSubmittedAnswers(answers);
            }}
          />
        </Collapse>
      </div>
    </NodeViewWrapper>
  );
};

export const CHECK_FOR_UNDERSTANDING_BLOCK = "checkForUnderstanding_block";

export const ChecksForUnderstanding = Node.create({
  name: CHECK_FOR_UNDERSTANDING_BLOCK,
  content: "text*",
  group: "inline",
  inline: true,
  atom: true,
  addAttributes() {
    return {
      isActiveQuizQuestion: { default: false },
      isCorrectSubmission: { default: false },
      quiz: { default: {} },
    };
  },
  renderHTML() {
    return ["span", 0];
  },
  addNodeView() {
    return ReactNodeViewRenderer(ChecksForUnderstandingComponent, {
      stopEvent: () => true, // CFU component events will NOT be propagated to the editor
    });
  },
});

// Size of the Checks for understanding node in the prosemirror tree. It's a constant that doesn't change
// regardless of its content because of `atom: true` setting of ChecksForUnderstanding node.
// https://tiptap.dev/docs/editor/api/schema#atom
export const checkForUnderstandingNodeSize = 2;
